/**
 * Weather Utilities (Premium Feature)
 *
 * Fetches real-time weather data and analyzes impact on lawn care recommendations.
 * Uses server-side API route (/api/weather) backed by Open-Meteo.
 *
 * Key features:
 * - Rain forecast → deprioritize watering
 * - Temperature extremes → adjust mowing/watering advice
 * - Wind speed → delay spraying/fertilizing
 * - UV index → best mowing times
 * - Soil moisture estimation → water scheduling
 * - 1-hour localStorage cache to minimize API calls
 */

import type { ActionCategory } from '@/lib/types/lawn-actions'

export interface WeatherData {
  current: {
    temp: number // °F
    humidity: number // %
    precipitation: number // inches (last hour)
  }
  forecast: {
    next24h: {
      precipChance: number // % (max in next 24h)
      precipAmount: number // inches
      tempHigh: number // °F
      tempLow: number // °F
    }
    next7days: {
      totalPrecip: number // inches
      avgTemp: number // °F
      /**
       * Seven-day mean soil temperature at 6cm (~2 in), the depth the
       * germination table in data/soil-germination-temps.ts is stated for.
       * Optional: payloads cached before the field existed omit it, and
       * Open-Meteo can return null hours for some cells.
       */
      soilTempF?: number | null
      daily: Array<{
        date: string
        tempHigh: number
        tempLow: number
        precipChance: number
        precipAmount: number
        windSpeed: number
        uvIndex: number
      }>
    }
  }
  wind: {
    currentSpeed: number // mph
    maxSpeed24h: number // mph
  }
  uv: {
    current: number // UV index (0-11+)
    max24h: number
  }
  timestamp: number // When fetched
}

export interface WeatherImpact {
  shouldSkipWatering: boolean
  adjustMowing: 'raise' | 'lower' | null
  delayApplication?: boolean // For fertilizer/herbicide due to wind
  soilMoisture?: 'dry' | 'adequate' | 'wet'
  bestMowingTime?: string // Based on UV/temp
  urgentAlert?: string
  message?: string // User-facing explanation
}

/**
 * Fetch weather data from server-side API route
 * Caches result in localStorage for 1 hour
 */
export async function fetchWeather(zipCode: string): Promise<WeatherData | null> {
  // Check cache first
  const cached = getCachedWeather(zipCode)
  if (cached) return cached

  try {
    const response = await fetch(`/api/weather?zip=${zipCode}`)
    if (!response.ok) throw new Error('Weather API request failed')

    const weatherData: WeatherData = await response.json()

    // Cache for 1 hour
    setCachedWeather(zipCode, weatherData)

    return weatherData
  } catch (error) {
    console.error('Failed to fetch weather:', error)
    return null
  }
}

/**
 * Get cached weather data if still valid (< 1 hour old)
 */
export function getCachedWeather(zipCode: string): WeatherData | null {
  try {
    const cached = localStorage.getItem(`weather:${zipCode}`)
    if (!cached) return null

    const data: WeatherData = JSON.parse(cached)

    // Check if cache is still valid (1 hour = 3600000ms)
    const age = Date.now() - data.timestamp
    if (age > 3600000) {
      localStorage.removeItem(`weather:${zipCode}`)
      return null
    }

    return data
  } catch {
    return null
  }
}

/**
 * Cache weather data in localStorage
 */
function setCachedWeather(zipCode: string, data: WeatherData): void {
  try {
    localStorage.setItem(`weather:${zipCode}`, JSON.stringify(data))
  } catch {
    // Ignore localStorage errors (quota, disabled, etc.)
  }
}

/**
 * Estimate soil moisture based on recent rainfall and evapotranspiration
 */
function estimateSoilMoisture(weather: WeatherData): 'dry' | 'adequate' | 'wet' {
  const last7DaysRain = weather.forecast.next7days.totalPrecip
  const avgTemp = weather.forecast.next7days.avgTemp

  // Simple ET estimation: higher temp = more evaporation
  const etFactor = avgTemp > 80 ? 1.5 : avgTemp > 65 ? 1.0 : 0.7
  const effectiveRain = last7DaysRain / etFactor

  if (effectiveRain < 0.5) return 'dry'
  if (effectiveRain > 1.5) return 'wet'
  return 'adequate'
}

/**
 * Determine best mowing time based on UV and temperature
 */
function getBestMowingTime(weather: WeatherData): string | undefined {
  const uvIndex = weather.uv.max24h
  const tempHigh = weather.forecast.next24h.tempHigh

  if (uvIndex >= 8 || tempHigh >= 85) {
    return 'Early morning (6-9 AM) to avoid heat stress'
  }
  return undefined
}

/**
 * Analyze weather impact on a specific lawn action
 */
export function analyzeWeatherImpact(
  weather: WeatherData,
  actionCategory: ActionCategory,
  grassSeason: 'Cool' | 'Warm'
): WeatherImpact {
  const impact: WeatherImpact = {
    shouldSkipWatering: false,
    adjustMowing: null,
  }

  const soilMoisture = estimateSoilMoisture(weather)
  impact.soilMoisture = soilMoisture

  // Rain coming → skip watering
  if (actionCategory === 'watering') {
    if (weather.forecast.next24h.precipChance >= 60) {
      impact.shouldSkipWatering = true
      impact.message = `Rain expected (${weather.forecast.next24h.precipAmount.toFixed(1)}" forecast)`
    } else if (soilMoisture === 'wet') {
      impact.shouldSkipWatering = true
      impact.message = `Soil moisture adequate from recent rainfall (${weather.forecast.next7days.totalPrecip.toFixed(1)}" last 7 days)`
    } else if (soilMoisture === 'dry') {
      impact.message = 'Soil is dry — water deeply today'
    } else if (weather.forecast.next7days.totalPrecip >= 1.0) {
      impact.message = `${weather.forecast.next7days.totalPrecip.toFixed(1)}" rain forecast this week`
    }
  }

  // Heat wave → raise mowing height
  if (actionCategory === 'mowing') {
    if (weather.forecast.next24h.tempHigh >= 90) {
      impact.adjustMowing = 'raise'
      impact.message = `Heat stress: raise mowing height 0.5-1" (${weather.forecast.next24h.tempHigh}°F expected)`
    }

    const bestTime = getBestMowingTime(weather)
    if (bestTime) {
      impact.bestMowingTime = bestTime
    }
  }

  // Wind too high → delay fertilizer/herbicide application
  if (actionCategory === 'fertilizing' || actionCategory === 'weed-control') {
    if (weather.wind.maxSpeed24h >= 10) {
      impact.delayApplication = true
      impact.message = `High winds expected (${weather.wind.maxSpeed24h} mph) — delay application to prevent drift`
    } else if (weather.wind.currentSpeed >= 8) {
      impact.message = `Winds ${weather.wind.currentSpeed} mph — apply with caution`
    }
  }

  // Frost warning for warm-season grasses
  if (grassSeason === 'Warm' && weather.forecast.next24h.tempLow <= 35) {
    impact.urgentAlert = `⚠️ Frost warning: ${weather.forecast.next24h.tempLow}°F tonight`
  }

  // Heat stress for cool-season grasses
  if (grassSeason === 'Cool' && weather.current.temp >= 85) {
    impact.urgentAlert = `🌡️ Heat stress: ${weather.current.temp}°F — water deeply if needed`
  }

  return impact
}

/**
 * Format weather data for display
 */
export function formatWeatherSummary(weather: WeatherData): string {
  return `${weather.current.temp}°F, ${weather.current.humidity}% humidity`
}
